/**
 * Sync Conflict Resolution Manager
 * Handles conflicts when offline data is synced with server
 */

// Conflict types
export const CONFLICT_TYPES = {
  CLOCK_TIME_MISMATCH: 'clock_time_mismatch',
  DUPLICATE_ENTRY: 'duplicate_entry',
  SERVER_MODIFIED: 'server_modified',
  DATA_STALE: 'data_stale',
  GPS_LOCATION_MISMATCH: 'gps_location_mismatch'
};

// Resolution strategies
export const RESOLUTION_STRATEGIES = {
  USE_LOCAL: 'use_local',
  USE_SERVER: 'use_server',
  MERGE: 'merge',
  MANUAL: 'manual'
};

class SyncConflictResolver {
  constructor() {
    this.conflicts = [];
    this.resolutionHistory = [];
    this.listeners = [];
  }

  // Add a conflict listener
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  notifyListeners(conflicts) {
    this.listeners.forEach(callback => callback(conflicts));
  }

  // Detect conflicts between local and server data
  detectConflicts(localData, serverData, dataType) {
    const conflicts = [];

    switch (dataType) {
      case 'CLOCK_IN':
      case 'CLOCK_OUT':
        conflicts.push(...this.detectClockConflicts(localData, serverData));
        break;
      case 'VITALS':
        conflicts.push(...this.detectVitalsConflicts(localData, serverData));
        break;
      case 'ADL':
        conflicts.push(...this.detectADLConflicts(localData, serverData));
        break;
      case 'NOTES':
        conflicts.push(...this.detectNotesConflicts(localData, serverData));
        break;
      default:
        break;
    }

    return conflicts;
  }

  detectClockConflicts(localData, serverData) {
    const conflicts = [];

    // Check if server already has a clock entry for this shift
    if (serverData && serverData.clock_in_time && localData.type === 'CLOCK_IN') {
      const localTime = new Date(localData.timestamp);
      const serverTime = new Date(serverData.clock_in_time);
      const timeDiff = Math.abs(localTime - serverTime) / 1000 / 60; // minutes

      if (timeDiff > 5) {
        conflicts.push({
          id: `clock_${localData.data.shiftId}_${Date.now()}`,
          type: CONFLICT_TYPES.CLOCK_TIME_MISMATCH,
          dataType: 'CLOCK_IN',
          localData: localData,
          serverData: serverData,
          localTimestamp: localData.timestamp,
          serverTimestamp: serverData.clock_in_time,
          timeDifferenceMinutes: timeDiff,
          severity: timeDiff > 30 ? 'high' : 'medium',
          suggestedResolution: timeDiff > 30 ? RESOLUTION_STRATEGIES.MANUAL : RESOLUTION_STRATEGIES.USE_LOCAL,
          message: `Clock-in time differs by ${Math.round(timeDiff)} minutes`
        });
      }
    }

    // Check for duplicate clock-out
    if (serverData && serverData.clock_out_time && localData.type === 'CLOCK_OUT') {
      conflicts.push({
        id: `clock_out_${localData.data.shiftId}_${Date.now()}`,
        type: CONFLICT_TYPES.DUPLICATE_ENTRY,
        dataType: 'CLOCK_OUT',
        localData: localData,
        serverData: serverData,
        severity: 'high',
        suggestedResolution: RESOLUTION_STRATEGIES.MANUAL,
        message: 'Shift was already clocked out on server'
      });
    }

    // GPS location mismatch
    if (localData.data.gps_location && serverData?.expected_location) {
      const distance = this.calculateDistance(
        localData.data.gps_location,
        serverData.expected_location
      );
      if (distance > 0.5) { // More than 0.5 km
        conflicts.push({
          id: `gps_${localData.data.shiftId}_${Date.now()}`,
          type: CONFLICT_TYPES.GPS_LOCATION_MISMATCH,
          dataType: localData.type,
          localData: localData,
          serverData: serverData,
          distanceKm: distance,
          severity: distance > 2 ? 'high' : 'low',
          suggestedResolution: RESOLUTION_STRATEGIES.MANUAL,
          message: `GPS location is ${distance.toFixed(2)}km from expected location`
        });
      }
    }

    return conflicts;
  }

  detectVitalsConflicts(localData, serverData) {
    const conflicts = [];

    if (serverData?.latestVitals) {
      const serverTime = new Date(serverData.latestVitals.recorded_at);
      const localTime = new Date(localData.timestamp);
      const timeDiff = Math.abs(localTime - serverTime) / 1000 / 60;

      // Check for duplicate entries within 10 minutes
      if (timeDiff < 10) {
        conflicts.push({
          id: `vitals_${localData.clientId}_${Date.now()}`,
          type: CONFLICT_TYPES.DUPLICATE_ENTRY,
          dataType: 'VITALS',
          localData: localData,
          serverData: serverData.latestVitals,
          severity: 'medium',
          suggestedResolution: RESOLUTION_STRATEGIES.MERGE,
          message: 'Similar vitals entry exists within 10 minutes'
        });
      }
    }

    return conflicts;
  }

  detectADLConflicts(localData, serverData) {
    const conflicts = [];

    if (serverData?.completedTasks) {
      const localTasks = new Set(localData.data.tasks || []);
      const serverTasks = new Set(serverData.completedTasks);
      
      const alreadyCompleted = [...localTasks].filter(t => serverTasks.has(t));
      
      if (alreadyCompleted.length > 0) {
        conflicts.push({
          id: `adl_${localData.shiftId}_${Date.now()}`,
          type: CONFLICT_TYPES.DUPLICATE_ENTRY,
          dataType: 'ADL',
          localData: localData,
          serverData: serverData,
          duplicateTasks: alreadyCompleted,
          severity: 'low',
          suggestedResolution: RESOLUTION_STRATEGIES.MERGE,
          message: `${alreadyCompleted.length} ADL tasks were already completed on server`
        });
      }
    }

    return conflicts;
  }

  detectNotesConflicts(localData, serverData) {
    const conflicts = [];

    if (serverData?.recentNotes) {
      // Check for very similar notes (potential duplicate)
      const recentNote = serverData.recentNotes.find(note => {
        const timeDiff = Math.abs(new Date(note.created_at) - new Date(localData.timestamp)) / 1000 / 60;
        return timeDiff < 30;
      });

      if (recentNote) {
        conflicts.push({
          id: `notes_${localData.clientId}_${Date.now()}`,
          type: CONFLICT_TYPES.DUPLICATE_ENTRY,
          dataType: 'NOTES',
          localData: localData,
          serverData: recentNote,
          severity: 'low',
          suggestedResolution: RESOLUTION_STRATEGIES.MANUAL,
          message: 'Similar progress note exists within 30 minutes'
        });
      }
    }

    return conflicts;
  }

  // Calculate distance between two GPS coordinates (Haversine formula)
  calculateDistance(coord1, coord2) {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(coord2.latitude - coord1.latitude);
    const dLon = this.toRad(coord2.longitude - coord1.longitude);
    const lat1 = this.toRad(coord1.latitude);
    const lat2 = this.toRad(coord2.latitude);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  toRad(deg) {
    return deg * (Math.PI / 180);
  }

  // Add a conflict to the queue
  addConflict(conflict) {
    this.conflicts.push({
      ...conflict,
      createdAt: new Date().toISOString(),
      resolved: false
    });
    this.notifyListeners(this.conflicts);
  }

  // Get all unresolved conflicts
  getUnresolvedConflicts() {
    return this.conflicts.filter(c => !c.resolved);
  }

  // Get conflicts by type
  getConflictsByType(type) {
    return this.conflicts.filter(c => c.type === type && !c.resolved);
  }

  // Resolve a conflict
  async resolveConflict(conflictId, resolution, customData = null) {
    const conflictIndex = this.conflicts.findIndex(c => c.id === conflictId);
    if (conflictIndex === -1) return { success: false, error: 'Conflict not found' };

    const conflict = this.conflicts[conflictIndex];
    let result;

    try {
      switch (resolution) {
        case RESOLUTION_STRATEGIES.USE_LOCAL:
          result = await this.applyLocalData(conflict);
          break;
        case RESOLUTION_STRATEGIES.USE_SERVER:
          result = await this.discardLocalData(conflict);
          break;
        case RESOLUTION_STRATEGIES.MERGE:
          result = await this.mergeData(conflict, customData);
          break;
        case RESOLUTION_STRATEGIES.MANUAL:
          result = await this.applyManualResolution(conflict, customData);
          break;
        default:
          throw new Error('Unknown resolution strategy');
      }

      // Mark as resolved
      this.conflicts[conflictIndex] = {
        ...conflict,
        resolved: true,
        resolvedAt: new Date().toISOString(),
        resolution: resolution,
        result: result
      };

      // Add to history
      this.resolutionHistory.push(this.conflicts[conflictIndex]);

      this.notifyListeners(this.conflicts);
      return { success: true, result };

    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async applyLocalData(conflict) {
    // Force sync local data to server
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('authToken');

    switch (conflict.dataType) {
      case 'CLOCK_IN':
        await fetch(`${API_URL}/api/shifts/${conflict.localData.data.shiftId}/clock-in?force=true`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...conflict.localData.data,
            force_override: true,
            offline_timestamp: conflict.localData.timestamp
          })
        });
        break;
      case 'CLOCK_OUT':
        await fetch(`${API_URL}/api/shifts/${conflict.localData.data.shiftId}/clock-out?force=true`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...conflict.localData.data,
            force_override: true,
            offline_timestamp: conflict.localData.timestamp
          })
        });
        break;
      default:
        break;
    }

    return { applied: 'local' };
  }

  async discardLocalData(conflict) {
    // Remove from local queue without syncing
    return { applied: 'server', discarded: conflict.localData };
  }

  async mergeData(conflict, customData) {
    // Merge local and server data
    const merged = {
      ...conflict.serverData,
      ...conflict.localData.data,
      ...customData,
      merged_at: new Date().toISOString()
    };

    return { applied: 'merged', data: merged };
  }

  async applyManualResolution(conflict, customData) {
    // Apply admin's manual resolution
    return { applied: 'manual', customData };
  }

  // Export conflicts for admin review
  exportConflicts() {
    return {
      unresolved: this.getUnresolvedConflicts(),
      resolved: this.resolutionHistory,
      exportedAt: new Date().toISOString()
    };
  }

  // Clear resolved conflicts older than specified days
  clearOldResolved(days = 30) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    this.resolutionHistory = this.resolutionHistory.filter(
      c => new Date(c.resolvedAt) > cutoff
    );
  }
}

// Create singleton instance
const syncConflictResolver = new SyncConflictResolver();

export default syncConflictResolver;
